import * as React from 'react';
import { graphql } from 'react-apollo';
import type { OptionProps } from 'react-apollo';

import { branch, compose, withProps } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import contentfulGql from 'js/lib/contentfulGql';
import hoistNonReactStatics from 'js/lib/hoistNonReactStatics';
import { getCmsLocaleString } from 'js/lib/language';

import type { BrowseEntityPageContentProps } from 'bundles/browse/components/Domain/types/Cms';
import type { PropsFromCaller as BrowseFaqPropsFromCaller } from 'bundles/browse/components/Faq/BrowseFAQs';
import { getGrowthCmsGraphqlContext } from 'bundles/cms/utils/CmsUtils';

type ContentfulVariables = Omit<BrowseFaqPropsFromCaller, 'faqTopic'>;

type PropsFromRouter = {
  router: InjectedRouter;
  isPreview?: boolean;
};

type PropsFromStores = {
  pageContent?: BrowseEntityPageContentProps;
};

type Props = PropsFromRouter & BrowseFaqPropsFromCaller & PropsFromStores;

const BrowseFAQQuery = contentfulGql`#graphql
  query BrowseFAQQuery(
    $preview: Boolean!
    $contentfulPageId: String!
    $pageTypeSlug: String!
    $locale: String!
    $domainSlug: String
  ) {
    browseEntityPageCollection(
      preview: $preview
      locale: $locale
      where: { slug: $contentfulPageId, pageTypeSlug: $pageTypeSlug, domainSlug: $domainSlug }
    ) {
      items {
        domainSlug
        subdomainSlug
        slug
        faqsCollection {
          items {
            sectionName
            question
            answer
          }
        }
      }
    }
  }
`;

const EntityFAQQuery = contentfulGql`#graphql
  query BrowseEntityFAQQuery($preview: Boolean!, $contentfulPageId: String!, $pageTypeSlug: String!, $locale: String!) {
    browseEntityPageCollection(
      preview: $preview
      locale: $locale
      where: { slug: $contentfulPageId, pageTypeSlug: $pageTypeSlug }
    ) {
      items {
        slug
        faqsCollection {
          items {
            sectionName
            question
            answer
          }
        }
      }
    }
  }
`;

const faqQueryOptions = {
  options: ({ isPreview }: $TSFixMe) => {
    return {
      context: getGrowthCmsGraphqlContext(isPreview),
    };
  },
  props: ({ data }: OptionProps<$TSFixMe>) => {
    const resp = data?.browseEntityPageCollection?.items[0];

    return {
      // Map gql data to fluxible response shape so we can reuse BrowseFAQs presentational component.
      pageContent: {
        domainSlug: resp?.domainSlug,
        subdomainSlug: resp?.subdomainSlug,
        slug: resp?.slug,
        faqs: resp?.faqsCollection?.items || [],
      },
    };
  },
};

const withGraphqlBrowseEntityContentfulFaqs =
  (getContentfulVariablesFromProps: (props: BrowseFaqPropsFromCaller) => ContentfulVariables) =>
  (BaseComponent: React.ComponentType<{}>) => {
    const componentName = BaseComponent.displayName || BaseComponent.name;

    class HOC extends React.Component<BrowseFaqPropsFromCaller> {
      static displayName = `withGraphqlBrowseEntityContentfulFaqs(${componentName})`;

      render() {
        // @ts-expect-error TS2559: Type '{ appSlug: string; faqTopic: string; isEntityPage?: boolean | undefined; faqSlug?: string | null | undefined; hideSchemaMarkup?: boolean | undefined; }' has no properties in common with type 'IntrinsicAttributes'.
        return <BaseComponent {...this.props} />;
      }
    }

    hoistNonReactStatics(HOC, BaseComponent);

    return compose<BrowseFaqPropsFromCaller, {}>(
      connectToRouter((router) => {
        const previewParam = router.location.query.preview;
        const isPreview = !!previewParam && Number(previewParam) === 1;
        return {
          isPreview,
          router,
        };
      }),
      compose(
        withProps((props: Props) => {
          const {
            router: { params, location },
            isPreview,
            faqSlug,
          } = props;

          const contentfulVariables = getContentfulVariablesFromProps(props);
          const { isEntityPage } = contentfulVariables;

          const domainSlug = params?.domainSlug;
          const subdomainSlug = params?.subdomainSlug;

          let contentfulPageId: string;

          if (isEntityPage) {
            const searchQuery = location.query.query;
            contentfulPageId = faqSlug || searchQuery;
          } else if (subdomainSlug) {
            contentfulPageId = subdomainSlug;
          } else {
            contentfulPageId = domainSlug;
          }

          return {
            locale: getCmsLocaleString(),
            pageTypeSlug: isEntityPage ? 'entity' : 'browse',
            preview: !!isPreview,
            domainSlug,
            contentfulPageId,
          };
        }),
        branch(
          ({ isEntityPage }: { isEntityPage?: boolean }) => !isEntityPage,
          graphql(BrowseFAQQuery, faqQueryOptions),
          graphql(EntityFAQQuery, faqQueryOptions)
        )
      )
    )(HOC);
  };

export default withGraphqlBrowseEntityContentfulFaqs;
